/**
 * Document Exporter Utility
 * Provides functions for exporting documents in various formats
 */

/**
 * Downloads content as a Markdown file
 * @param {string} content - Document content in markdown format
 * @param {string} fileName - Name of the file (without extension)
 */
export const downloadAsMarkdown = (content, fileName) => {
  try {
    // Create a blob with the markdown content
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.md`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Error downloading markdown:', error);
    throw new Error(`Failed to download Markdown: ${error.message}`);
  }
};

/**
 * Downloads content as a DOCX file
 * Since we can't generate proper DOCX in the browser,
 * this function creates a special HTML file that can be opened in Word
 * @param {string} content - Document content in markdown format
 * @param {string} fileName - Name of the file (without extension)
 */
export const downloadAsDocx = (content, fileName) => {
  try {
    // Convert markdown to simple HTML
    const html = convertMarkdownToHtml(content);

    // Create a full HTML document with Word-specific metadata
    const wordHtml = `
      <!DOCTYPE html>
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns:m="http://schemas.microsoft.com/office/2004/12/omml"
            xmlns:v="urn:schemas-microsoft-com:vml"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <meta name="ProgId" content="Word.Document">
        <meta name="Generator" content="Microsoft Word 15">
        <meta name="Originator" content="Microsoft Word 15">
        <title>${fileName}</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:TrackMoves>false</w:TrackMoves>
            <w:TrackFormatting/>
            <w:PunctuationKerning/>
            <w:ValidateAgainstSchemas/>
            <w:SaveIfXMLInvalid>false</w:SaveIfXMLInvalid>
            <w:IgnoreMixedContent>false</w:IgnoreMixedContent>
            <w:AlwaysShowPlaceholderText>false</w:AlwaysShowPlaceholderText>
            <w:DoNotPromoteQF/>
            <w:LidThemeOther>EN-US</w:LidThemeOther>
            <w:LidThemeAsian>X-NONE</w:LidThemeAsian>
            <w:LidThemeComplexScript>X-NONE</w:LidThemeComplexScript>
            <w:DoNotShadeFormData/>
            <w:DoNotPromoteQF/>
            <w:DoNotUsePrinterDefaults/>
            <w:Compatibility>
              <w:BreakWrappedTables/>
              <w:SnapToGridInCell/>
              <w:WrapTextWithPunct/>
              <w:UseAsianBreakRules/>
              <w:DontGrowAutofit/>
              <w:SplitPgBreakAndParaMark/>
              <w:EnableOpenTypeKerning/>
              <w:DontFlipMirrorIndents/>
              <w:OverrideTableStyleHps/>
            </w:Compatibility>
            <w:DoNotOptimizeForBrowser/>
            <m:mathPr>
              <m:mathFont m:val="Cambria Math"/>
              <m:brkBin m:val="before"/>
              <m:brkBinSub m:val="&#45;-"/>
              <m:smallFrac m:val="off"/>
              <m:dispDef/>
              <m:lMargin m:val="0"/>
              <m:rMargin m:val="0"/>
              <m:defJc m:val="centerGroup"/>
              <m:wrapIndent m:val="1440"/>
              <m:intLim m:val="subSup"/>
              <m:naryLim m:val="undOvr"/>
            </m:mathPr>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <!--[if gte mso 9]>
        <xml>
          <w:LatentStyles DefLockedState="false" DefUnhideWhenUsed="false"
            DefSemiHidden="false" DefQFormat="false" DefPriority="99"
            LatentStyleCount="376">
          </w:LatentStyles>
        </xml>
        <![endif]-->
        <style>
          /* Basic Styles */
          body {
            font-family: 'Calibri', 'sans-serif';
            font-size: 11pt;
            line-height: 1.5;
            margin: 1in;
          }
          /* Headings */
          h1, h2, h3, h4, h5, h6 {
            font-family: 'Calibri', 'sans-serif';
            font-weight: bold;
            margin-top: 12pt;
            margin-bottom: 6pt;
            page-break-after: avoid;
          }
          h1 {
            font-size: 16pt;
            color: #2E74B5;
            border-bottom: 1pt solid #DEDEDE;
            padding-bottom: 4pt;
          }
          h2 {
            font-size: 14pt;
            color: #2E74B5;
          }
          h3 {
            font-size: 12pt;
            color: #1F4E79;
          }
          h4, h5, h6 {
            font-size: 11pt;
            font-weight: bold;
          }
          /* Paragraphs */
          p {
            margin-top: 0;
            margin-bottom: 10pt;
          }
          /* Lists */
          ul, ol {
            margin-top: 0;
            margin-bottom: 10pt;
            padding-left: 24pt;
          }
          li {
            margin-bottom: 5pt;
          }
          /* Code Blocks */
          pre {
            font-family: 'Consolas', 'Courier New', monospace;
            font-size: 10pt;
            background-color: #F8F8F8;
            border: 1pt solid #DDDDDD;
            padding: 5pt;
            margin: 10pt 0;
            white-space: pre-wrap;
            page-break-inside: avoid;
          }
          code {
            font-family: 'Consolas', 'Courier New', monospace;
            font-size: 10pt;
            background-color: #F8F8F8;
            padding: 1pt 3pt;
            border: 1pt solid #DDDDDD;
          }
          /* Tables */
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 10pt 0;
            border: 1pt solid #DDDDDD;
          }
          table td, table th {
            border: 1pt solid #DDDDDD;
            padding: 5pt;
          }
          table th {
            background-color: #F8F8F8;
            font-weight: bold;
          }
          /* Document Title */
          .document-title {
            font-size: 20pt;
            font-weight: bold;
            color: #2E74B5;
            text-align: center;
            margin-bottom: 18pt;
          }
          /* Document Metadata */
          .document-meta {
            text-align: center;
            color: #666666;
            margin-bottom: 24pt;
            font-size: 9pt;
          }
          /* Add page break - MS Word specific */
          .page-break {
            page-break-before: always;
          }
          /* Block quotes */
          blockquote {
            margin-left: 18pt;
            margin-right: 18pt;
            font-style: italic;
            color: #666666;
            border-left: 3pt solid #DDDDDD;
            padding-left: 10pt;
          }
          /* MSO specific styles */
          @page Section1 {
            margin: 1.0in 1.0in 1.0in 1.0in;
            mso-header-margin: .5in;
            mso-footer-margin: .5in;
            mso-paper-source: 0;
          }
          div.Section1 {
            page: Section1;
          }
        </style>
      </head>
      <body lang="EN-US">
        <div class="Section1">
          <div class="document-title">${fileName}</div>
          <div class="document-meta">Generated on ${new Date().toLocaleDateString()} • ${new Date().toLocaleTimeString()}</div>

          ${html}

          <p style="text-align: center; color: #999999; font-size: 8pt; margin-top: 24pt;">
            Generated with document export functionality
          </p>
        </div>
      </body>
      </html>
    `;

    // Create a blob with the Word HTML
    const blob = new Blob([wordHtml], { type: 'application/vnd.ms-word' });
    const url = URL.createObjectURL(blob);

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.doc`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);

    return true;
  } catch (error) {
    console.error('Error downloading DOCX:', error);
    throw new Error(`Failed to download DOCX: ${error.message}`);
  }
};

/**
 * Simple markdown to HTML converter
 * @param {string} markdown - Markdown content
 * @returns {string} HTML content
 */
function convertMarkdownToHtml(markdown) {
  if (!markdown) return '';
  
  // Very basic markdown to HTML conversion
  let html = markdown
    // Replace headings
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    
    // Replace lists
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    
    // Replace bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\_\_(.+?)\_\_/g, '<strong>$1</strong>')
    .replace(/\_(.+?)\_/g, '<em>$1</em>')
    
    // Replace links
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>')
    
    // Replace code blocks
    .replace(/```(.+?)```/gs, '<pre>$1</pre>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    
    // Replace horizontal rules
    .replace(/^---$/gm, '<hr>')
    
    // Replace paragraphs (anything that's not a special element)
    .replace(/^([^<].+)$/gm, '<p>$1</p>');
  
  // Replace consecutive list items with ul/ol tags
  html = html.replace(/<li>.*?<\/li>(\s*<li>.*?<\/li>)+/g, (match) => {
    return '<ul>' + match + '</ul>';
  });
  
  return html;
}